// Kampf am Ufer (§6): Gelände (K49, Fabi, 26.10.2026). Steine und Treibholzstamm sind fest: Man läuft drumherum, indem
// man an der Kante entlanggleitet (kein Wegfinder), und gerade Geschosse bleiben daran hängen. Ins Gebüsch kann man
// hineingehen; wer darin steht, ist vor Fernkämpfern versteckt. Reine Geometrie, ohne Zustand.
import type { Obstacle, Unit } from '../types.ts';

type P = { x: number; y: number };

/** Der Punkt auf dem Kern des Hindernisses (Mitte oder Mittellinie des Stamms), der p am nächsten liegt */
export function closestOn(o: Obstacle, p: P): P {
  if (o.x2 === undefined || o.y2 === undefined) return { x: o.x, y: o.y };
  const dx = o.x2 - o.x;
  const dy = o.y2 - o.y;
  const len2 = dx * dx + dy * dy || 1;
  const k = Math.max(0, Math.min(1, ((p.x - o.x) * dx + (p.y - o.y) * dy) / len2));
  return { x: o.x + dx * k, y: o.y + dy * k };
}

/** Abstand von p zum Rand des Hindernisses (negativ: drin) */
export function edgeGap(o: Obstacle, p: P): number {
  const c = closestOn(o, p);
  return Math.hypot(p.x - c.x, p.y - c.y) - o.radius;
}

export const solid = (o: Obstacle) => o.kind !== 'bush';

/** Wer gerade unter der Erde wühlt, kommt unter allem durch */
const ignoresTerrain = (u: Unit) => u.travel?.kind === 'tunnel' || u.travel?.kind === 'beckon';

/**
 * K49: Gleiten statt Steckenbleiben. Führt der Schritt in ein festes Hindernis, geht er mit vollem Tempo an dessen Kante
 * entlang, auf der Seite, auf der das Ziel liegt; so kommt man um Steine und den Stamm herum
 */
export function steer(obstacles: Obstacle[], u: Unit, sx: number, sy: number): [number, number] {
  if (ignoresTerrain(u)) return [sx, sy];
  const len = Math.hypot(sx, sy);
  if (len < 1e-9) return [sx, sy];
  for (const o of obstacles) {
    if (!solid(o)) continue;
    const reach = o.radius + u.radius;
    if (edgeGap(o, { x: u.x + sx, y: u.y + sy }) + o.radius >= reach) continue;
    const c = closestOn(o, u);
    let nx = u.x - c.x;
    let ny = u.y - c.y;
    const nd = Math.hypot(nx, ny);
    if (nd < 1e-9) {
      nx = -sx / len;
      ny = -sy / len;
    } else {
      nx /= nd;
      ny /= nd;
    }
    const into = sx * nx + sy * ny;
    if (into >= 0) continue;
    const tx = -ny;
    const ty = nx;
    const along = sx * tx + sy * ty;
    const side = Math.abs(along) > len * 1e-3 ? Math.sign(along) : u.uid % 2 === 0 ? 1 : -1;
    sx = tx * side * len;
    sy = ty * side * len;
  }
  return [sx, sy];
}

/** Steckt jemand in einem festen Hindernis, kommt er an dessen Rand */
export function pushOut(obstacles: Obstacle[], u: Unit): void {
  if (ignoresTerrain(u)) return;
  for (const o of obstacles) {
    if (!solid(o)) continue;
    const c = closestOn(o, u);
    const reach = o.radius + u.radius;
    let dx = u.x - c.x;
    let dy = u.y - c.y;
    const d = Math.hypot(dx, dy);
    if (d >= reach) continue;
    if (d < 1e-9) {
      dx = -1;
      dy = 0;
    } else {
      dx /= d;
      dy /= d;
    }
    u.x = c.x + dx * reach;
    u.y = c.y + dy * reach;
  }
}

/** Ein Punkt außerhalb aller festen Hindernisse (für Befehle und Kniffe mit Ortsziel) */
export function freePoint(obstacles: Obstacle[], p: P, radius: number): P {
  const q = { ...p };
  for (const o of obstacles) {
    if (!solid(o)) continue;
    const c = closestOn(o, q);
    const reach = o.radius + radius;
    const d = Math.hypot(q.x - c.x, q.y - c.y);
    if (d >= reach) continue;
    const nx = d < 1e-9 ? -1 : (q.x - c.x) / d;
    const ny = d < 1e-9 ? 0 : (q.y - c.y) / d;
    q.x = c.x + nx * reach;
    q.y = c.y + ny * reach;
  }
  return q;
}

/** K49: Steht er im Gebüsch? (die Mitte der Figur im Busch) */
export function inBush(obstacles: Obstacle[] | undefined, u: P): boolean {
  if (!obstacles) return false;
  return obstacles.some((o) => o.kind === 'bush' && edgeGap(o, u) <= 0);
}

/** Feste Hindernisse, an denen er selbst steht: Aus der Deckung heraus schießt man darüber hinweg */
export function coverOf(obstacles: Obstacle[] | undefined, u: Unit, reach: number): string[] {
  if (!obstacles) return [];
  return obstacles.filter((o) => solid(o) && edgeGap(o, u) - u.radius <= reach).map((o) => o.id);
}

/** Das erste feste Hindernis auf der Linie von a nach b (ohne die übergangenen), in Schritten von höchstens einer Feldeinheit */
export function blockerOn(obstacles: Obstacle[] | undefined, a: P, b: P, ignore: string[] = []): Obstacle | null {
  if (!obstacles) return null;
  const list = obstacles.filter((o) => solid(o) && !ignore.includes(o.id));
  if (!list.length) return null;
  const d = Math.hypot(b.x - a.x, b.y - a.y);
  const steps = Math.max(1, Math.ceil(d));
  for (let i = 1; i <= steps; i++) {
    const p = { x: a.x + ((b.x - a.x) * i) / steps, y: a.y + ((b.y - a.y) * i) / steps };
    for (const o of list) if (edgeGap(o, p) <= 0) return o;
  }
  return null;
}

/** Das feste Hindernis, in dem der Punkt liegt (ohne die übergangenen) */
export function solidAt(obstacles: Obstacle[] | undefined, p: P, ignore: string[] = []): Obstacle | null {
  if (!obstacles) return null;
  for (const o of obstacles) if (solid(o) && !ignore.includes(o.id) && edgeGap(o, p) <= 0) return o;
  return null;
}
